import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAccount } from '../../contexts/Account';
import { getFeaturedCompany } from '../../services/advisor_services';
import BoardCard from '../BoardCard';
import BoardSkeleton from '../../components/SkeletonLoading/boardSkeleton';
import eventBus, { eventBusValues } from '../../eventBus';

const sanitizeCompanyLogo = (company = {}) => {
  if (company.companyLogo) return company.companyLogo;
  if (company.image?.location) {
    return company.image.location.replace(/^https?:\/\//, '');
  }
  return null;
};

const NewFeaturedBoards = (props) => {
  const { getContainerRef = null } = props;
  const { accountDetails } = useAccount();
  const [featuredBoards, setFeaturedBoards] = useState([]);
  const [hideScroll, setHideScroll] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [width, setWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);
  const isXs = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  const shouldMeasure = isLoading || featuredBoards.length > 0;

  useEffect(() => {
    if (!shouldMeasure) return undefined;

    const element = containerRef.current;
    if (!element) return undefined;

    const compute = () => {
      const rect = element.getBoundingClientRect();
      const measured = rect?.width || element.offsetWidth || 0;
      const viewportWidth = Math.max(0, (window.innerWidth || 0) - 80);
      const next =
        measured > 0 ? Math.min(measured, viewportWidth) : viewportWidth;
      setWidth(next);
    };

    // Initial read after paint to avoid 0 width
    requestAnimationFrame(compute);
    window.addEventListener('resize', compute);

    // Observe element resize for more robust updates
    const observer = new ResizeObserver(() => requestAnimationFrame(compute));
    observer.observe(element);

    return () => {
      window.removeEventListener('resize', compute);
      observer.disconnect();
    };
  }, [shouldMeasure]);

  useEffect(() => {
    if (getContainerRef) {
      getContainerRef(containerRef);
    }
  }, [containerRef]);

  useEffect(() => {
    if (!accountDetails?.id) return;
    setIsLoading(true);
    getFeaturedCompany({ ADVISOR_ID: accountDetails.id })
      .then((companies) => {
        const validBoards = companies.filter(
          (board) =>
            board &&
            board.boardOwnerStatus !== 'inactive' &&
            board.boardOwnerStatus !== 'paused' &&
            !['dormant', 'pending_cancellation', 'position_filled'].includes(
              board.opportunityStage
            )
        );
        const normalized = validBoards.map((board) => ({
          ...board,
          companyLogo: sanitizeCompanyLogo(board),
        }));
        setFeaturedBoards(normalized);
      })
      .catch((error) =>
        console.error('Error fetching featured companies:', error)
      )
      .finally(() => setIsLoading(false));
  }, [accountDetails?.id]);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollWidth, clientWidth } = container;
    const compareWidth = width || clientWidth || 0;
    setHideScroll(scrollWidth <= compareWidth);
  }, [width, featuredBoards, isXs, isLoading]);

  const sortedBoards = useMemo(() => {
    return [...featuredBoards].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : 0;
      const dateB = b.createdAt ? new Date(b.createdAt) : 0;
      return dateB - dateA;
    });
  }, [featuredBoards]);

  if (!isLoading && sortedBoards.length === 0) {
    return null;
  }

  const totalBoards = sortedBoards.length;
  let displayedBoards = sortedBoards;

  // if (isXs && !expanded) {
  //   if (totalBoards > 2) {
  //     displayedBoards = sortedBoards.slice(0, 2);
  //   }
  // }

  const showMoreButton = isXs && totalBoards > 2;

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const jump = 300;

    if (scrollLeft + clientWidth + 50 >= scrollWidth) {
      container.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      container.scrollBy({
        left: jump,
        behavior: 'smooth',
      });
    }
  };

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const jump = 300;
    if (container.scrollLeft === 0) {
      container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -jump, behavior: 'smooth' });
    }
  };

  const handleCardClick = (company) => {
    if (!company) return;
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company,
      companyId: company.id,
    });
  };

  const skeletonCount = isXs ? 1 : 3;

  const boardItems = isLoading
    ? Array.from({ length: skeletonCount }).map((_, index) => (
        <Box
          key={`featured-board-skeleton-${index}`}
          sx={{
            marginBottom: '20px',
            paddingTop: '10px',
            marginRight: '16px',
            width: { xxs: '280px', xs: '350px' },
          }}
        >
          <BoardSkeleton />
        </Box>
      ))
    : displayedBoards.map((board) => (
        <Box
          key={`featured-board-${board.id}`}
          sx={{
            marginBottom: '20px',
            paddingTop: '10px',
            marginRight: '16px',
            width: { xxs: '280px', xs: '350px' },
          }}
        >
          <BoardCard
            board={board}
            hideMemberSince
            companyList
            width={isXs ? '280px' : '350px'}
            onClick={() => handleCardClick(board)}
            displayPillsFlag
          />
        </Box>
      ));

  return (
    <Paper
      id="new-featured-boards-tile"
      ref={containerRef}
      elevation={1}
      sx={{
        padding: '15px',
        paddingRight: { xxs: '26px', xs: '15px' },
        borderRadius: { xxs: '0', xs: '12px' },
        marginBottom: '20px',
        boxShadow: 'none',
        width: { xxs: '110%', xs: '100%' },
        position: 'relative',
        border: '1px solid #E0E0E0',
        display: isLoading || sortedBoards.length > 0 ? 'block' : 'none',
        marginLeft: { xxs: '-10px', xs: '0px' },
      }}
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: '20px', mb: '4px', color: '#001030' }}
      >
        📣 New & Featured Boards
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: 14,
          color: '#4D586E',
          fontStyle: 'italic',
          mb: '16px',
        }}
      >
        New advisory boards are added to AdvisoryCloud every week! We’ll also
        highlight boards here that have exciting developments and are seeking
        new advisors.
      </Typography>

      {/* {!hideScroll && boardItems.length > 1 && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            left: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )} */}

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'visible',
          whiteSpace: 'nowrap',
          scrollBehavior: 'smooth',
          overscrollBehaviorX: 'contain',
          marginRight: { xxs: 0, xs: hideScroll ? 0 : '40px' },
          maxWidth: {
            xxs: '100%',
            xs: width ? width - 40 : '100%',
            md: width ? width + 200 : '100%',
            lg: width ? width + 500 : '100%',
          },
        }}
      >
        {boardItems}
      </Box>

      {!hideScroll && boardItems.length > 1 && (
        <IconButton
          onClick={scrollRight}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            right: 15,
            bottom: 45,
            height: 20,
            width: 20,
            transform: 'translateY(-600%)',
            zIndex: 10,
            color: '#000',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {/* {showMoreButton && (
        <Box sx={{ textAlign: 'center', mt: '0px' }}>
          <Button
            variant="text"
            onClick={() => setExpanded(!expanded)}
            sx={{
              border: '1px solid #2C71F64D',
              borderRadius: '8px',
              color: '#2C71F6',
              backgroundColor: '#fff',
              p: '4px 16px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              width: '100%',
              height: '40px',
              '&:hover': { backgroundColor: '#F3F4F6' },
            }}
          >
            {expanded ? 'Less' : 'More'}
          </Button>
        </Box>
      )} */}
    </Paper>
  );
};

export default NewFeaturedBoards;
